import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import {
  ActionBar,
  Navigator,
  SplitView,
  StatusBar,
  readStored,
  writeStored,
  type DesktopAction,
  type NavNode,
} from '@/components/desktop';
import { ErrorNote, OkNote, errorMessage, shortDate, todayIso } from '@/components/ui';
import { addDaysIso, hrGet, monthName, useFarmOptions, useHrInvalidation, useHrLookups } from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import { confirmDiscard } from '@/stores/confirm';
import { useFarmScope } from '@/stores/farmScope';
import { useScreenIntent } from '@/stores/shellNav';
import { AttendanceMonth, type MonthData } from './AttendanceMonth';
import { AttendanceRegisterGrid } from './AttendanceRegisterGrid';
import {
  lockReason,
  noWork,
  sameValues,
  serverValues,
  type RegisterRow,
  type RowValues,
} from './attendanceRegister';
import { DraftNotice } from '@/components/DraftNotice';
import { useDraft } from '@/stores/drafts';
import { hrPut, problemList, useUnsavedFlag, yearOptions } from './hrScreenHelpers';
import './attendance.css';

const FARM_KEY = 'poultry.hr.attendance.farm';
const VIEW_KEY = 'poultry.hr.attendance.view';
const HEAD_OFFICE = 'HEAD_OFFICE';

type View = 'list' | 'month';

interface Register {
  date: string;
  farmId: string;
  farm: string;
  holiday: string | null;
  future: boolean;
  rows: RegisterRow[];
  totals: {
    staff: number;
    marked: number;
    present: number;
    absent: number;
    halfDay: number;
    leave: number;
    off: number;
  };
}

const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: monthName(i + 1) }));

/**
 * Attendance: each farm's daily register, a month view of it, and a
 * typed-in month for farms that keep a paper register.
 *
 * The register is built for a supervisor with a hundred people to mark every
 * morning: "Mark the rest present", then P / A / H / L / O on the exceptions,
 * all from the keyboard, and Ctrl+S. Days a submitted pay run covers are
 * locked, and days of approved leave belong to the leave record.
 */
export function Attendance() {
  const { can } = useAuth();
  const allFarms = useAuth((s) => s.user?.scope.allFarms ?? false);
  const invalidate = useHrInvalidation();
  const queryClient = useQueryClient();
  const topFarmIds = useFarmScope((s) => s.selectedFarmIds);
  const today = todayIso();

  // Sent here for one farm (from the HR dashboard, say): open that farm.
  const intent = useScreenIntent('hr-att');
  const [farmPick, setFarmPick] = useState<string | null>(
    () => intent?.farmId ?? (topFarmIds.length === 1 ? topFarmIds[0]! : null) ?? readStored<string | null>(FARM_KEY, null),
  );
  const [date, setDate] = useState(today);
  const [view, setViewState] = useState<View>(() => (readStored<string>(VIEW_KEY, 'list') === 'month' ? 'month' : 'list'));
  const [ym, setYm] = useState(() => ({ year: Number(today.slice(0, 4)), month: Number(today.slice(5, 7)) }));
  const [edits, setEdits] = useState<Record<string, RowValues>>({});
  const [note, setNote] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);

  const farms = useFarmOptions();
  const lookups = useHrLookups();
  const farmList = farms.data ?? [];
  // The tree lists only the farms the top bar has in view; head office is not
  // a farm the top bar can select, so narrowing to specific farms there drops
  // it, the same way picking a farm always narrows rather than widens.
  const visibleFarmList = topFarmIds.length === 0 ? farmList : farmList.filter((f) => topFarmIds.includes(f.id));
  const headOfficeVisible = allFarms && topFarmIds.length === 0;
  const validIds = [...(headOfficeVisible ? [HEAD_OFFICE] : []), ...visibleFarmList.map((f) => f.id)];
  // Nothing remembered (or no longer visible): the first farm, then head office.
  const fallbackFarm = visibleFarmList[0]?.id ?? (headOfficeVisible ? HEAD_OFFICE : null);
  const farmId = farms.isSuccess ? (farmPick && validIds.includes(farmPick) ? farmPick : fallbackFarm) : null;
  const farmRow = visibleFarmList.find((f) => f.id === farmId);
  const farmLabel = farmId === HEAD_OFFICE ? 'Head office' : farmRow ? `${farmRow.code} · ${farmRow.nameEn}` : '';

  // Changing the top bar to exactly one farm follows it here too; several
  // farms (or "All farms") leaves whatever this screen already had, only
  // narrowed to still being one of them (handled by `validIds` above).
  const prevTopFarmKey = useRef(topFarmIds.join(','));
  useEffect(() => {
    const key = topFarmIds.join(',');
    if (prevTopFarmKey.current === key) return;
    prevTopFarmKey.current = key;
    if (topFarmIds.length === 1) {
      leaveRegister(() => {
        setFarmPick(topFarmIds[0]!);
        writeStored(FARM_KEY, topFarmIds[0]!);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topFarmIds]);

  const canEdit = can('hr.attendance.update');

  // A whole day's register for a farm is a lot of ticking. The draft is named
  // after the farm and the day, so switching either shows that day's own
  // unsaved marks rather than yesterday's.
  const draft = useDraft(
    `attendance.${farmId ?? 'none'}.${date}`,
    edits,
    (saved) => setEdits(saved),
    { enabled: farmId !== null && canEdit },
  );

  const register = useQuery({
    queryKey: ['hr', 'attendance', 'register', farmId, date],
    queryFn: () => hrGet<Register>('/api/v1/hr/attendance/register', { farmId, date }),
    enabled: view === 'list' && farmId !== null,
  });
  const month = useQuery({
    queryKey: ['hr', 'attendance', 'month', farmId, ym.year, ym.month],
    queryFn: () => hrGet<MonthData>('/api/v1/hr/attendance/month', { farmId, year: ym.year, month: ym.month }),
    enabled: view === 'month' && farmId !== null,
  });

  const rows = register.data?.rows ?? [];
  const leaveTypes = lookups.data?.leaveTypes ?? [];

  // Only edits that still differ from what is saved count, and only for
  // people still on the register.
  const changed = rows.filter((r) => edits[r.employeeId] !== undefined);
  const dirty = changed.length > 0;
  useUnsavedFlag(dirty);

  const valuesOf = (r: RegisterRow): RowValues => edits[r.employeeId] ?? serverValues(r);

  function change(row: RegisterRow, patch: Partial<RowValues>) {
    setNote(null);
    setEdits((prev) => {
      const base = prev[row.employeeId] ?? serverValues(row);
      const next: RowValues = { ...base, ...patch };
      if (patch.mark !== undefined) {
        if (next.mark !== 'LEAVE') next.leaveTypeId = null;
        if (noWork(next.mark)) next.overtimeHours = '0';
      }
      const copy = { ...prev };
      if (sameValues(next, serverValues(row))) delete copy[row.employeeId];
      else copy[row.employeeId] = next;
      return copy;
    });
  }

  /** Runs `action`, first asking before throwing away unsaved marks. */
  function leaveRegister(action: () => void) {
    if (!dirty) {
      action();
      return;
    }
    void confirmDiscard().then((ok) => {
      if (!ok) return;
      setEdits({});
      draft.forget();
      action();
    });
  }

  function pickFarm(id: string) {
    if (id === farmId) return;
    leaveRegister(() => {
      setFarmPick(id);
      writeStored(FARM_KEY, id);
      setNote(null);
      setProblems([]);
    });
  }

  function pickDate(next: string) {
    if (!next || next === date) return;
    const d = next > today ? today : next;
    leaveRegister(() => {
      setDate(d);
      setNote(null);
      setProblems([]);
    });
  }

  function setView(next: View) {
    if (next === view) return;
    leaveRegister(() => {
      setViewState(next);
      writeStored(VIEW_KEY, next);
      if (next === 'month') setYm({ year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) });
      setNote(null);
      setProblems([]);
    });
  }

  function openDate(d: string) {
    setDate(d > today ? today : d);
    setViewState('list');
    writeStored(VIEW_KEY, 'list');
    setNote(null);
    setProblems([]);
  }

  const save = useMutation({
    mutationFn: async (payload: Array<Record<string, unknown>>) => {
      const result = await hrPutRegister(farmId!, date, payload);
      // Wait for the fresh register before dropping the edits, so the grid
      // never flickers back to the old marks.
      await queryClient.invalidateQueries({ queryKey: ['hr', 'attendance', 'register', farmId, date] });
      return result;
    },
    onSuccess: (r) => {
      setEdits({});
      draft.forget();
      invalidate();
      setProblems([]);
      setNote(
        `Saved ${r.saved} ${r.saved === 1 ? 'person' : 'people'}${r.cleared ? `, cleared ${r.cleared}` : ''} for ${shortDate(date)}.`,
      );
    },
    onError: (e) => setProblems(problemList(e, 'Could not save the register')),
  });

  const fill = useMutation({
    mutationFn: () =>
      api.post<{ saved: number }>('/api/v1/hr/attendance/register/fill', { farmId, date }),
    onSuccess: (r) => {
      invalidate();
      setProblems([]);
      setNote(
        r.saved === 0
          ? 'Everyone was already marked.'
          : `Marked ${r.saved} ${r.saved === 1 ? 'person' : 'people'}: holidays and weekly offs as such, everyone else present. Change anyone who was not.`,
      );
    },
    onError: (e) => setProblems(problemList(e, 'Could not mark the rest')),
  });

  function trySave() {
    if (!dirty || save.isPending || !canEdit || farmId === null) return;
    const issues: string[] = [];
    for (const r of changed) {
      const v = edits[r.employeeId]!;
      if (v.mark === 'LEAVE' && !v.leaveTypeId) issues.push(`${r.nameEn}: choose which leave`);
      const ot = Number(v.overtimeHours || 0);
      if (!Number.isFinite(ot) || ot < 0 || ot > 24) issues.push(`${r.nameEn}: overtime must be 0 to 24 hours`);
    }
    if (issues.length > 0) {
      setProblems(issues);
      return;
    }
    save.mutate(
      changed.map((r) => {
        const v = edits[r.employeeId]!;
        return {
          employeeId: r.employeeId,
          mark: v.mark,
          leaveTypeId: v.mark === 'LEAVE' ? v.leaveTypeId : null,
          overtimeHours: String(Number(v.overtimeHours || 0)),
          note: v.note.trim() || null,
          shiftId: r.shiftId,
        };
      }),
    );
  }

  // Ctrl+S saves the register from anywhere on the screen.
  const saveRef = useRef(trySave);
  useEffect(() => {
    saveRef.current = trySave;
  });
  useEffect(() => {
    if (view !== 'list') return;
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveRef.current();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view]);

  // ------------------------------------------------------------ counts

  const effective = rows.map((r) => valuesOf(r).mark);
  const count = (m: string) => effective.filter((x) => x === m).length;
  const marked = effective.filter((x) => x !== null).length;
  const toMark = rows.filter((r) => !r.saved && !r.lockedBy).length;
  const editable = rows.some((r) => !lockReason(r, canEdit));

  // ------------------------------------------------------------ layout

  const nodes: NavNode[] = [
    ...(headOfficeVisible
      ? [{ id: HEAD_OFFICE, label: 'Head office', icon: <AppIcon kind="department" />, hint: 'Staff not placed on a farm' }]
      : []),
    ...visibleFarmList.map((f) => ({ id: f.id, label: `${f.code} · ${f.nameEn}`, icon: <AppIcon kind="farm" /> })),
  ];

  const navigator = farms.isError ? (
    <div className="stack">
      <ErrorNote message={farms.error.message} />
    </div>
  ) : (
    <Navigator id="hr-attendance" label="Farms" nodes={nodes} selectedId={farmId ?? ''} onSelect={pickFarm} />
  );

  const toggle = (
    <div className="viewtoggle" role="tablist" aria-label="View">
      {(['list', 'month'] as const).map((v) => (
        <button
          key={v}
          type="button"
          role="tab"
          aria-selected={view === v}
          className={`viewtoggle__btn${view === v ? ' viewtoggle__btn--on' : ''}`}
          onClick={() => setView(v)}
        >
          {v === 'list' ? 'List' : 'Month'}
        </button>
      ))}
    </div>
  );

  const listActions: DesktopAction[] = [
    {
      label: 'Mark the rest present',
      icon: 'checklist',
      onSelect: () => fill.mutate(),
      allowed: can('hr.attendance.create'),
      enabled: !dirty && toMark > 0 && !fill.isPending && farmId !== null,
      reason: dirty ? 'Save or undo your changes first' : 'Everyone is already marked',
    },
    {
      label: 'Save',
      icon: 'post',
      primary: true,
      shortcut: 'Ctrl+S',
      onSelect: trySave,
      allowed: canEdit,
      enabled: dirty && !save.isPending,
      reason: 'Nothing has changed yet',
    },
    {
      label: 'Undo changes',
      icon: 'reverse',
      onSelect: () => {
        setEdits({});
        draft.forget();
        setProblems([]);
      },
      allowed: canEdit,
      enabled: dirty,
      reason: 'Nothing has changed yet',
    },
  ];

  const main = (
    <div className="workbench__main">
      <DraftNotice
        draft={draft}
        onStartAgain={() => {
          setEdits({});
          setProblems([]);
        }}
      />
      <div className="filterbar attbar">
        {view === 'list' ? (
          <div className="attbar__nav">
            <button type="button" className="btn btn--sm" onClick={() => pickDate(addDaysIso(date, -1))}>
              ← Previous day
            </button>
            <label className="field">
              <span className="field__label">Date</span>
              <input
                className="field__input"
                type="date"
                value={date}
                max={today}
                onChange={(e) => pickDate(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn--sm"
              disabled={date >= today}
              title={date >= today ? 'Attendance cannot be marked for a day that has not come yet' : undefined}
              onClick={() => pickDate(addDaysIso(date, 1))}
            >
              Next day →
            </button>
            {date !== today && (
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => pickDate(today)}>
                Today
              </button>
            )}
          </div>
        ) : (
          <div className="attbar__nav">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => setYm((c) => (c.month === 1 ? { year: c.year - 1, month: 12 } : { year: c.year, month: c.month - 1 }))}
            >
              ← Previous month
            </button>
            <label className="field">
              <span className="field__label">Month</span>
              <select
                className="field__input"
                value={String(ym.month)}
                onChange={(e) => setYm((c) => ({ ...c, month: Number(e.target.value) }))}
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Year</span>
              <select
                className="field__input"
                value={String(ym.year)}
                onChange={(e) => setYm((c) => ({ ...c, year: Number(e.target.value) }))}
              >
                {yearOptions(Number(today.slice(0, 4)), 5, 0).map((y) => (
                  <option key={y.value} value={y.value}>
                    {y.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn--sm"
              disabled={`${ym.year}-${String(ym.month).padStart(2, '0')}` >= today.slice(0, 7)}
              onClick={() => setYm((c) => (c.month === 12 ? { year: c.year + 1, month: 1 } : { year: c.year, month: c.month + 1 }))}
            >
              Next month →
            </button>
          </div>
        )}
        {toggle}
      </div>

      {view === 'list' ? (
        <>
          <ActionBar actions={listActions}>
            <span className="muted attbar__keys">Keys: P A H L O mark · Del clears · ↑ ↓ move · Ctrl+S saves</span>
          </ActionBar>
          {register.data?.holiday && (
            <p className="attbar__holiday">
              <AppIcon kind="month" size={14} /> Holiday: {register.data.holiday}. Everyone not marked shows it as a suggestion.
            </p>
          )}
          {note && <OkNote message={note} />}
          {problems.map((p) => (
            <ErrorNote key={p} message={p} />
          ))}
          {register.error && <ErrorNote message={errorMessage(register.error, 'Could not load the register')} />}
          {farmId === null && farms.isSuccess ? (
            <p className="muted grid__empty">You have no farm to mark attendance for.</p>
          ) : register.isPending && register.fetchStatus !== 'idle' ? (
            <p className="muted">Loading…</p>
          ) : rows.length === 0 && register.isSuccess ? (
            <p className="muted grid__empty">Nobody works here on {shortDate(date)}.</p>
          ) : rows.length > 0 ? (
            <AttendanceRegisterGrid
              rows={rows}
              valuesOf={valuesOf}
              isDirty={(r) => edits[r.employeeId] !== undefined}
              onChange={change}
              leaveTypes={leaveTypes}
              canEdit={canEdit}
            />
          ) : null}
        </>
      ) : (
        <>
          {month.error && <ErrorNote message={errorMessage(month.error, 'Could not load the month')} />}
          <AttendanceMonth
            data={month.data}
            loading={month.isPending && month.fetchStatus !== 'idle'}
            farmLabel={farmLabel}
            today={today}
            canUpdate={canEdit}
            onOpenDate={openDate}
          />
        </>
      )}
    </div>
  );

  const monthRows = month.data?.rows ?? [];

  return (
    <div className="workbench">
      <SplitView id="hr-attendance" initial={240} firstLabel="farm list" first={navigator} second={main} />
      <StatusBar>
        <span className="withicon">
          <AppIcon kind={farmId === HEAD_OFFICE ? 'department' : 'farm'} size={14} />
          {farmLabel || 'No farm chosen'}
        </span>
        {view === 'list' ? (
          <>
            <span>{shortDate(date)}</span>
            <span>{rows.length} staff</span>
            <span>{marked} marked</span>
            <span>{count('PRESENT')} present</span>
            <span>{count('ABSENT')} absent</span>
            <span>{count('HALF_DAY')} half day</span>
            <span>{count('LEAVE')} on leave</span>
            <span>{count('WEEKLY_OFF') + count('HOLIDAY')} off</span>
            <span>{rows.length - marked} still to mark</span>
            {register.data?.holiday && <span>Holiday: {register.data.holiday}</span>}
            {dirty && (
              <span className="statusbar__note">
                {changed.length} {changed.length === 1 ? 'change' : 'changes'} not saved
              </span>
            )}
            {!dirty && rows.length > 0 && !editable && <span>Nothing here can be changed</span>}
          </>
        ) : (
          <>
            <span>
              {monthName(ym.month)} {ym.year}
            </span>
            <span>{monthRows.length} staff</span>
            <span>{monthRows.reduce((t, r) => t + r.totals.unmarked, 0)} days not marked</span>
            <span>{monthRows.filter((r) => r.override).length} typed-in months</span>
            {monthRows.some((r) => r.lockedBy) && <span>{monthRows.filter((r) => r.lockedBy).length} locked by a pay run</span>}
          </>
        )}
      </StatusBar>
    </div>
  );
}

function hrPutRegister(farmId: string, date: string, rows: Array<Record<string, unknown>>) {
  return hrPut<{ date: string; saved: number; cleared: number }>('/api/v1/hr/attendance/register', { farmId, date, rows });
}
